import { useCallback, useRef } from "react";

interface UseLongPressOptions {
  readonly onLongPress?: (rect: DOMRect) => void;
  readonly duration?: number;
  readonly moveThreshold?: number;
}

export interface LongPressHandlers {
  readonly onTouchStart: (e: React.TouchEvent) => void;
  readonly onTouchMove: (e: React.TouchEvent) => void;
  readonly onTouchEnd: () => void;
}

export function useLongPress(options: UseLongPressOptions): LongPressHandlers {
  const { onLongPress, duration = 500, moveThreshold = 10 } = options;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const targetRef = useRef<HTMLElement | null>(null);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      startXRef.current = e.touches[0].clientX;
      startYRef.current = e.touches[0].clientY;
      targetRef.current = e.currentTarget as HTMLElement;

      timerRef.current = setTimeout(() => {
        if (onLongPress && targetRef.current) {
          onLongPress(targetRef.current.getBoundingClientRect());
        }
      }, duration);
    },
    [onLongPress, duration],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const dx = Math.abs(e.touches[0].clientX - startXRef.current);
      const dy = Math.abs(e.touches[0].clientY - startYRef.current);

      if (dx > moveThreshold || dy > moveThreshold) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }
    },
    [moveThreshold],
  );

  const onTouchEnd = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
